import React, { useState, useEffect, useRef, Fragment } from "react";
import { useAuth } from "../../contexts/AuthContext";
import {
  getCycleLogs,
  addCycleLog,
  updateCycleLog,
  deleteCycleLog,
  calculateCycleStats,
} from "../../services/cycleService";
import { CycleLog, CycleStats } from "../../types/cycle";
import {
  PlusIcon,
  TrashIcon,
  PencilIcon,
  QuestionMarkCircleIcon,
  XMarkIcon,
  EllipsisVerticalIcon,
} from "@heroicons/react/24/outline";
import { Dialog, Transition } from "@headlessui/react";
import { HiXMark } from "react-icons/hi2";
import CurrentCycleCard from "./CurrentCycleCard";
import MoonPhaseCard from "./MoonPhaseCard";

const Cycle: React.FC = () => {
  const { currentUser } = useAuth();
  const [cycleLogs, setCycleLogs] = useState<CycleLog[]>([]);
  const [stats, setStats] = useState<CycleStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingLog, setEditingLog] = useState<CycleLog | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [cycleToDelete, setCycleToDelete] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showHelpDialog, setShowHelpDialog] = useState(false);
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  const [historyPage, setHistoryPage] = useState(0);
  const HISTORY_PAGE_SIZE = 6;
  const menuRef = useRef<HTMLDivElement>(null);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setOpenMenuId(null);
      }
    };
    if (openMenuId) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [openMenuId]);

  // Form state
  const [startDate, setStartDate] = useState("");
  const [notes, setNotes] = useState("");

  useEffect(() => {
    if (currentUser) {
      loadCycleLogs();
    }
  }, [currentUser]);

  const loadCycleLogs = async () => {
    if (!currentUser) return;

    try {
      setLoading(true);
      const logs = await getCycleLogs(currentUser.uid);
      setCycleLogs(logs);

      const calculatedStats = calculateCycleStats(logs);
      setStats(calculatedStats);
    } catch (error) {
      console.error("Error loading cycle logs:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddCycle = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!currentUser || !startDate || submitting) return;

    try {
      setSubmitting(true);
      await addCycleLog(currentUser.uid, {
        cycle_start_date: startDate,
        notes: notes || undefined,
      });

      // Reset form
      setStartDate("");
      setNotes("");
      setShowAddForm(false);

      // Reload logs
      await loadCycleLogs();
    } catch (error) {
      console.error("Error adding cycle log:", error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdateCycle = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!currentUser || !editingLog || !startDate || submitting) return;

    try {
      setSubmitting(true);
      await updateCycleLog(currentUser.uid, editingLog.id!, {
        cycle_start_date: startDate,
        notes: notes || undefined,
      });

      // Reset form
      setStartDate("");
      setNotes("");
      setEditingLog(null);

      // Reload logs
      await loadCycleLogs();
    } catch (error) {
      console.error("Error updating cycle log:", error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteClick = (logId: string) => {
    setCycleToDelete(logId);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!currentUser || !cycleToDelete) return;

    try {
      setIsDeleting(true);
      await deleteCycleLog(currentUser.uid, cycleToDelete);
      await loadCycleLogs();
      setDeleteDialogOpen(false);
      setCycleToDelete(null);
    } catch (error) {
      console.error("Error deleting cycle log:", error);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleCancelDelete = () => {
    setDeleteDialogOpen(false);
    setCycleToDelete(null);
  };

  const handleEditClick = (log: CycleLog) => {
    setEditingLog(log);
    // setStartDate(log.cycle_start_date);
    setStartDate(log.cycle_start_date || log.cycleStartDate || "");
    setNotes(log.notes || "");
    setShowAddForm(false);
  };

  const handleCancelEdit = () => {
    setEditingLog(null);
    setStartDate("");
    setNotes("");
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    const day = date.getDate();
    const month = date.toLocaleDateString("en-US", { month: "short" });
    const year = date.getFullYear();
    return `${day} ${month} ${year}`;
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="loading loading-spinner loading-lg"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-base-200 pb-20 pt-20">
      {/* Header */}
      <div className="sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-8 py-4 pb-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <h1 className="text-4xl font-heading font-semibold text-base-content">
                My Cycle
              </h1>
              <button
                onClick={() => setShowHelpDialog(true)}
                className="p-2 text-base-content/80 hover:text-pink-500 transition-colors rounded-full hover:bg-pink-500/10"
                title="Learn about cycle phases"
              >
                <QuestionMarkCircleIcon className="w-6 h-6" />
              </button>
            </div>
            {!showAddForm && !editingLog && (
              <button
                onClick={() => setShowAddForm(true)}
                className="bg-pink-500 text-white px-4 py-2 rounded-lg font-sans font-medium hover:bg-pink-500/90 transition-colors flex items-center gap-2 text-sm"
              >
                <PlusIcon className="w-4 h-4" />
                Log Cycle
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="max-w-4xl sm:px-8 mx-auto py-0">
        {/* Current Cycle Progress */}
        {stats && stats.most_recent_cycle_start_date && (
          <CurrentCycleCard
            most_recent_cycle_start_date={stats.most_recent_cycle_start_date}
            average_cycle_length={stats.average_cycle_length}
            className="mb-4"
          />
        )}

        {/* Statistics Card */}
        {stats && stats.total_cycles > 0 && (
          <div className="pb-8 mb-4 sm:rounded-lg p-8 py-6 bg-base-100 shadow-sm">
            <h3 className="text-sm font-heading mb-4">
              My Cycle{" "}
              <span className="font-grotesk text-base-content/60">Stats</span>
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 items-center">
              <div className="border-l pl-4">
                <p className="text-xs text-base-content/60 font-sans">
                  Average Length
                </p>
                <p className="text-lg font-heading font-bold">
                  {stats.average_cycle_length > 0
                    ? `${stats.average_cycle_length} days`
                    : "N/A"}
                </p>
              </div>
              <div className="border-l pl-4">
                <p className="text-xs text-base-content/60 font-sans">
                  Shortest Cycle
                </p>
                <p className="text-lg font-heading font-bold">
                  {stats.shortest_cycle > 0
                    ? `${stats.shortest_cycle} days`
                    : "N/A"}
                </p>
              </div>
              <div className="border-l pl-4">
                <p className="text-xs text-base-content/60 font-sans">
                  Longest Cycle
                </p>
                <p className="text-lg font-heading font-bold">
                  {stats.longest_cycle > 0
                    ? `${stats.longest_cycle} days`
                    : "N/A"}
                </p>
              </div>
              {/* <div>
                <p className="text-xs text-base-content/60 font-sans">
                  Total Cycles
                </p>
                <p className="text-2xl font-heading font-bold">
                  {stats.total_cycles}
                </p>
              </div> */}
              {stats.predicted_next_cycle_date && (
                <div className="border-l pl-4">
                  <p className="text-xs text-base-content/60 font-sans">
                    Predicted Next Cycle
                  </p>
                  <p className="text-lg font-heading font-semibold text-pink-500">
                    {formatDate(stats.predicted_next_cycle_date)}
                  </p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Add/Edit Form Dialog */}
        <Transition appear show={showAddForm || !!editingLog} as={Fragment}>
          <Dialog
            as="div"
            className="relative z-50"
            onClose={() => {
              setShowAddForm(false);
              handleCancelEdit();
            }}
          >
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-200"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="ease-in duration-150"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm" />
            </Transition.Child>

            <div className="fixed inset-0 overflow-y-auto">
              <div className="flex h-full md:min-h-full md:items-center md:justify-center md:p-4 items-end">
                <Transition.Child
                  as={Fragment}
                  enter="ease-out duration-200"
                  enterFrom="opacity-0 md:scale-95"
                  enterTo="opacity-100 md:scale-100"
                  leave="ease-in duration-150"
                  leaveFrom="opacity-100 md:scale-100"
                  leaveTo="opacity-0 md:scale-95"
                >
                  <Dialog.Panel className="w-full md:max-w-md transform overflow-hidden md:rounded-2xl bg-base-100 shadow-xl transition-all flex flex-col h-[calc(100%-4rem)] md:h-auto mb-0 md:mt-0 rounded-t-2xl">
                    {/* Header */}
                    <div className="flex items-center justify-between p-6 pb-0 px-8 flex-shrink-0">
                      <Dialog.Title className="text-xl font-sans font-bold text-base-content">
                        {editingLog ? "Edit Cycle" : "Log Cycle"}
                      </Dialog.Title>
                      <button
                        onClick={() => {
                          setShowAddForm(false);
                          handleCancelEdit();
                        }}
                        disabled={submitting}
                        className="p-2 text-base-content/50 hover:text-base-content/80 rounded-full hover:bg-base-200 transition-colors disabled:opacity-50"
                      >
                        <HiXMark className="w-5 h-5" />
                      </button>
                    </div>

                    {/* Form Content */}
                    <form
                      onSubmit={editingLog ? handleUpdateCycle : handleAddCycle}
                      className="flex flex-col flex-1"
                    >
                      <div className="p-8 space-y-4 pt-6 flex-1">
                        <div>
                          <p className="text-xs text-base-content/80 mb-2 ml-1 font-sans">
                            <strong>Start Date</strong>
                          </p>
                          <input
                            type="date"
                            value={startDate}
                            onChange={(e) => setStartDate(e.target.value)}
                            required
                            className="w-full p-4 rounded-xl bg-base-200 border-none focus:outline-none focus:ring-0 font-sans text-base-content text-[16px] sm:text-sm"
                            placeholder="Select start date"
                            onClick={(e) => {
                              e.currentTarget.showPicker?.();
                            }}
                          />
                          <p className="text-xs text-base-content/60 mt-1 ml-1 font-sans">
                            Cycle length will be calculated when you add your
                            next cycle
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-base-content/80 mb-2 ml-1 font-sans">
                            <strong>Notes</strong> (Optional)
                          </p>
                          <textarea
                            value={notes}
                            onChange={(e) => setNotes(e.target.value)}
                            rows={3}
                            placeholder="Add any notes about this cycle..."
                            className="w-full p-4 rounded-xl bg-base-200 border-none resize-none focus:outline-none focus:ring-0 font-sans text-base-content text-[16px] sm:text-sm"
                            style={{ minHeight: "80px" }}
                          />
                        </div>
                      </div>

                      {/* Footer Actions */}
                      <div className="flex gap-2 p-8 pt-0 pb-8 flex-shrink-0 mb-16 md:mb-0">
                        <button
                          type="submit"
                          disabled={submitting}
                          className="flex-1 bg-pink-500 text-white px-4 py-3 md:py-2 rounded-md font-sans font-medium hover:bg-pink-500/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                        >
                          {submitting && (
                            <svg
                              className="animate-spin h-5 w-5"
                              xmlns="http://www.w3.org/2000/svg"
                              fill="none"
                              viewBox="0 0 24 24"
                            >
                              <circle
                                className="opacity-25"
                                cx="12"
                                cy="12"
                                r="10"
                                stroke="currentColor"
                                strokeWidth="4"
                              ></circle>
                              <path
                                className="opacity-75"
                                fill="currentColor"
                                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                              ></path>
                            </svg>
                          )}
                          {submitting
                            ? editingLog
                              ? "Updating..."
                              : "Adding..."
                            : editingLog
                              ? "Update Cycle"
                              : "Log Cycle"}
                        </button>
                      </div>
                    </form>
                  </Dialog.Panel>
                </Transition.Child>
              </div>
            </div>
          </Dialog>
        </Transition>

        {/* Moon Phase */}
        <MoonPhaseCard
          cycleDay={
            stats && stats.most_recent_cycle_start_date
              ? Math.floor(
                  (new Date().getTime() -
                    new Date(stats.most_recent_cycle_start_date).getTime()) /
                    (1000 * 60 * 60 * 24),
                ) + 1
              : undefined
          }
          className="mb-6"
        />

        {/* Cycle History */}
        <div className="bg-transparent overflow-hidden pb-32">
          <div className="px-6 py-4 border-b border-base-200">
            <h2 className="text-lg font-grotesk font-bold">
              Cycle <span className="font-light">History</span>
            </h2>
          </div>

          {cycleLogs.length === 0 ? (
            <div className="px-6 py-12 text-center">
              <p className="text-base-content/60 font-sans">
                No cycle logs yet.
              </p>
              <p className="text-sm text-base-content/50 font-sans mt-2">
                Start tracking your cycle to see insights and predictions.
              </p>
            </div>
          ) : (
            <div className="divide-y divide-base-300">
              {(() => {
                const sorted = [...cycleLogs].sort((a, b) => {
                  const dateA = a.cycle_start_date || a.cycleStartDate || "";
                  const dateB = b.cycle_start_date || b.cycleStartDate || "";
                  return dateB.localeCompare(dateA);
                });
                const mostRecentId = sorted[0]?.id;
                const paged = sorted.slice(
                  historyPage * HISTORY_PAGE_SIZE,
                  (historyPage + 1) * HISTORY_PAGE_SIZE,
                );
                return paged.map((log) => (
                  <div
                    key={log.id}
                    className="px-6 py-3 hover:bg-base-200 transition-colors"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3">
                          <div>
                            <p className="font-grotesk font-semibold text-base-content">
                              {log.cycle_start_date || log.cycleStartDate
                                ? formatDate(
                                    log.cycle_start_date ||
                                      log.cycleStartDate ||
                                      "",
                                  )
                                : "Unknown date"}
                            </p>
                            {log.notes && (
                              <p className="text-xs text-base-content/50 font-sans mt-0.5 line-clamp-1">
                                {log.notes}
                              </p>
                            )}
                          </div>
                          {log.id === mostRecentId ? (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-sans font-medium bg-pink-500/15 text-pink-700 dark:text-pink-400">
                              Current
                            </span>
                          ) : log.cycle_length ? (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-sans font-medium bg-pink-500/10 text-pink-500">
                              {log.cycle_length} days
                            </span>
                          ) : null}
                        </div>
                      </div>
                      <div
                        className="relative ml-4"
                        ref={openMenuId === log.id ? menuRef : undefined}
                      >
                        <button
                          onClick={() =>
                            setOpenMenuId(
                              openMenuId === log.id ? null : log.id!,
                            )
                          }
                          className="p-2 text-base-content/50 hover:text-base-content/80 transition-colors rounded-full hover:bg-base-200"
                        >
                          <EllipsisVerticalIcon className="w-5 h-5" />
                        </button>
                        {openMenuId === log.id && (
                          <div className="absolute right-0 top-full mt-1 z-20 bg-base-100 rounded-lg shadow-lg border border-base-300 overflow-hidden min-w-[140px]">
                            <button
                              onClick={() => {
                                setOpenMenuId(null);
                                handleEditClick(log);
                              }}
                              className="flex items-center gap-2 w-full px-4 py-2.5 text-sm font-sans text-base-content hover:bg-base-200 transition-colors"
                            >
                              <PencilIcon className="w-4 h-4" />
                              Edit
                            </button>
                            <button
                              onClick={() => {
                                setOpenMenuId(null);
                                handleDeleteClick(log.id!);
                              }}
                              className="flex items-center gap-2 w-full px-4 py-2.5 text-sm font-sans text-red-600 hover:bg-red-50 dark:hover:bg-red-500/10 transition-colors"
                            >
                              <TrashIcon className="w-4 h-4" />
                              Delete
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ));
              })()}
            </div>
          )}
          {cycleLogs.length > HISTORY_PAGE_SIZE && (
            <div className="flex items-center justify-between px-6 py-3 border-t border-base-200">
              <button
                onClick={() => setHistoryPage((p) => Math.max(0, p - 1))}
                disabled={historyPage === 0}
                className="text-sm font-sans text-base-content/60 hover:text-base-content disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
              >
                ← Previous
              </button>
              <span className="text-xs font-sans text-base-content/50">
                {historyPage + 1} /{" "}
                {Math.ceil(cycleLogs.length / HISTORY_PAGE_SIZE)}
              </span>
              <button
                onClick={() =>
                  setHistoryPage((p) =>
                    Math.min(
                      Math.ceil(cycleLogs.length / HISTORY_PAGE_SIZE) - 1,
                      p + 1,
                    ),
                  )
                }
                disabled={
                  historyPage >=
                  Math.ceil(cycleLogs.length / HISTORY_PAGE_SIZE) - 1
                }
                className="text-sm font-sans text-base-content/60 hover:text-base-content disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
              >
                Next →
              </button>
            </div>
          )}
        </div>

        {/* Delete Confirmation Dialog */}
        <Transition appear show={deleteDialogOpen} as={Fragment}>
          <Dialog
            as="div"
            className="relative z-50"
            onClose={handleCancelDelete}
          >
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-200"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="ease-in duration-150"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm" />
            </Transition.Child>

            <div className="fixed inset-0 overflow-y-auto">
              <div className="flex min-h-full items-center justify-center p-4">
                <Transition.Child
                  as={Fragment}
                  enter="ease-out duration-200"
                  enterFrom="opacity-0 scale-95"
                  enterTo="opacity-100 scale-100"
                  leave="ease-in duration-150"
                  leaveFrom="opacity-100 scale-100"
                  leaveTo="opacity-0 scale-95"
                >
                  <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-base-100 shadow-xl transition-all">
                    {/* Header */}
                    <div className="flex items-center gap-3 p-6 pb-2">
                      <TrashIcon className="w-6 h-6 text-red-500" />
                      <Dialog.Title className="text-xl font-heading font-semibold text-base-content">
                        Confirm Deletion
                      </Dialog.Title>
                    </div>

                    {/* Content */}
                    <div className="px-6 pb-2">
                      <p className="text-sm text-base-content/80 font-sans">
                        Are you sure you want to delete this cycle log? This
                        action cannot be undone.
                      </p>
                    </div>

                    {/* Actions */}
                    <div className="flex justify-between gap-3 p-6 pt-4">
                      <button
                        onClick={handleCancelDelete}
                        className="bg-base-200 px-4 py-2 rounded-md font-sans font-medium hover:bg-base-300 transition-colors"
                        disabled={isDeleting}
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleConfirmDelete}
                        className="bg-red-500/15 text-red-600 px-4 py-2 rounded-md font-sans font-medium hover:bg-red-200 transition-colors disabled:opacity-50 flex items-center gap-2"
                        disabled={isDeleting}
                      >
                        {isDeleting && (
                          <svg
                            className="animate-spin h-4 w-4"
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                          >
                            <circle
                              className="opacity-25"
                              cx="12"
                              cy="12"
                              r="10"
                              stroke="currentColor"
                              strokeWidth="4"
                            ></circle>
                            <path
                              className="opacity-75"
                              fill="currentColor"
                              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                            ></path>
                          </svg>
                        )}
                        {isDeleting ? "Deleting..." : "Delete"}
                      </button>
                    </div>
                  </Dialog.Panel>
                </Transition.Child>
              </div>
            </div>
          </Dialog>
        </Transition>

        {/* Help Dialog */}
        <Transition appear show={showHelpDialog} as={Fragment}>
          <Dialog
            as="div"
            className="relative z-50"
            onClose={() => setShowHelpDialog(false)}
          >
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-200"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="ease-in duration-150"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm" />
            </Transition.Child>

            <div className="fixed inset-0 overflow-y-auto">
              <div className="flex min-h-full items-center justify-center p-4">
                <Transition.Child
                  as={Fragment}
                  enter="ease-out duration-200"
                  enterFrom="opacity-0 scale-95"
                  enterTo="opacity-100 scale-100"
                  leave="ease-in duration-150"
                  leaveFrom="opacity-100 scale-100"
                  leaveTo="opacity-0 scale-95"
                >
                  <Dialog.Panel className="w-full max-w-2xl transform overflow-hidden rounded-md bg-base-100 shadow-xl transition-all">
                    {/* Header */}
                    <div className="flex items-center justify-between p-8 pb-0 border-base-200">
                      <Dialog.Title className="text-xl font-grotesk font-bold text-base-content">
                        Understanding Your Cycle Phases
                      </Dialog.Title>
                      <button
                        onClick={() => setShowHelpDialog(false)}
                        className="p-2 text-base-content/50 hover:text-base-content/80 rounded-full hover:bg-base-200 transition-colors"
                      >
                        <XMarkIcon className="w-5 h-5" />
                      </button>
                    </div>

                    {/* Content */}
                    <div className="p-8 pt-4 space-y-6 max-h-[70vh] overflow-y-auto">
                      {/* Menstruation Phase */}
                      <div className="space-y-2">
                        <div className="flex items-center gap-2">
                          <h3 className="text-lg font-sans font-semibold text-base-content">
                            Menstruation
                          </h3>
                          <span className="text-sm text-base-content/60 font-sans">
                            (Days 1-4 approx.)
                          </span>
                        </div>
                        <p className="text-sm text-base-content/80 font-sans">
                          Low hormones increase inflammation and may heighten
                          perceived effort. Training tolerance varies widely, so
                          tune into your symptoms.
                        </p>
                      </div>

                      {/* Early Follicular Phase */}
                      <div className="space-y-2">
                        <div className="flex items-center gap-2">
                          <h3 className="text-lg font-sans font-semibold text-base-content">
                            Early Follicular
                          </h3>
                          <span className="text-sm text-base-content/60 font-sans">
                            (Days 5-8 approx.)
                          </span>
                        </div>
                        <p className="text-sm text-base-content/80 font-sans">
                          Rising oestrogen and settling inflammation often bring
                          improved energy and stable recovery. This phase
                          supports building volume and refining skills.
                        </p>
                      </div>

                      {/* Late Follicular Phase */}
                      <div className="space-y-2">
                        <div className="flex items-center gap-2">
                          <h3 className="text-lg font-sans font-semibold text-base-content">
                            Late Follicular
                          </h3>
                          <span className="text-sm text-base-content/60 font-sans">
                            (Days 9-13 approx.)
                          </span>
                        </div>
                        <p className="text-sm text-base-content/80 font-sans">
                          Peak oestrogen enhances neuromuscular function and
                          carbohydrate use. Strength, power, and high-intensity
                          work often feel more accessible.
                        </p>
                      </div>

                      {/* Ovulatory Phase */}
                      <div className="space-y-2">
                        <div className="flex items-center gap-2">
                          <h3 className="text-lg font-sans font-semibold text-base-content">
                            Ovulatory
                          </h3>
                          <span className="text-sm text-base-content/60 font-sans">
                            (Days 14-15 approx.)
                          </span>
                        </div>
                        <p className="text-sm text-base-content/80 font-sans">
                          High oestrogen supports performance capacity, though
                          connective tissue changes may affect joint stability.
                          Movement quality awareness helps manage load.
                        </p>
                      </div>

                      {/* Early Luteal Phase */}
                      <div className="space-y-2">
                        <div className="flex items-center gap-2">
                          <h3 className="text-lg font-sans font-semibold text-base-content">
                            Early Luteal
                          </h3>
                          <span className="text-sm text-base-content/60 font-sans">
                            (Days 16-21 approx.)
                          </span>
                        </div>
                        <p className="text-sm text-base-content/80 font-sans">
                          Rising progesterone elevates body temperature and
                          shifts to fat metabolism. Cardiovascular strain may
                          feel higher, making hydration and recovery key.
                        </p>
                      </div>

                      {/* Late Luteal Phase */}
                      <div className="space-y-2">
                        <div className="flex items-center">
                          <h3 className="text-lg font-sans font-semibold text-base-content">
                            Late Luteal
                          </h3>
                          <span className="text-sm text-base-content/60 font-sans">
                            (Days 22-28 approx.)
                          </span>
                        </div>
                        <p className="text-sm text-base-content/80 font-sans">
                          Oestrogen and progesterone levels are decreasing,
                          while inflammatory markers, fluid retention, and
                          nervous system sensitivity may increase. Fatigue and
                          reduced stress tolerance are common, so be open to
                          training flexibility around how you are feeling.
                        </p>
                      </div>

                      {/* Info Box */}
                      <div className="">
                        <p className="text-sm text-pink-700 dark:text-pink-400 font-sans">
                          <strong className="font-semibold">Note:</strong> These
                          phases are calculated based on your average cycle
                          length and may vary from person to person. The day
                          ranges shown are approximate for a 28-day cycle and
                          will scale to your individual cycle length.
                        </p>
                      </div>
                    </div>

                    {/* Footer */}
                    <div className="flex justify-end p-6 pt-0">
                      <button
                        onClick={() => setShowHelpDialog(false)}
                        className="px-4 font-grotesk py-2 text-sm  text-white bg-neutral rounded-lg hover:bg-neutral transition-colors"
                      >
                        Okay
                      </button>
                    </div>
                  </Dialog.Panel>
                </Transition.Child>
              </div>
            </div>
          </Dialog>
        </Transition>
      </div>
    </div>
  );
};

export default Cycle;
